import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import {
  ClipboardList, FileClock, CheckCircle2, TrendingUp, Calendar,
  BarChart3, PieChart as PieIcon, AlertTriangle, Clock, Send,
  ShieldCheck, XCircle, FileX, FileText,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid, Legend,
} from "recharts";
import StatusBadge from "@/components/StatusBadge";

// Master KPI palette per user spec — each card is fully filled with the color.
// {bg, text, icon}. Creamy White uses dark text; all others use white.
const KPI_STYLE = {
  "Total Leads":         { bg: "#F5C517", text: "#1F1400", icon: ClipboardList },  // YELLOW
  "New Case":            { bg: "#2563EB", text: "#FFFFFF", icon: FileClock },       // BLUE
  "Document Pending":    { bg: "#FFF6D1", text: "#3B2E00", icon: FileText },        // CREAMY WHITE (dark text)
  "Work in Process":     { bg: "#F97316", text: "#FFFFFF", icon: TrendingUp },      // ORANGE
  "Sent for Approval":   { bg: "#EC4899", text: "#FFFFFF", icon: Send },            // PINK
  "Approved":            { bg: "#166534", text: "#FFFFFF", icon: ShieldCheck },     // DARK GREEN
  "Completed":           { bg: "#22C55E", text: "#FFFFFF", icon: CheckCircle2 },    // GREEN
  "Not Approved":        { bg: "#B91C1C", text: "#FFFFFF", icon: XCircle },         // DEEP RED
  "On Hold":             { bg: "#7C4A17", text: "#FFFFFF", icon: Clock },           // BROWN
  "Chalan Pending":      { bg: "#6B7280", text: "#FFFFFF", icon: FileX },           // GREY
  "Cancel":              { bg: "#EF4444", text: "#FFFFFF", icon: XCircle },         // RED
  "Today's Order":       { bg: "#8B5CF6", text: "#FFFFFF", icon: Calendar },        // PURPLE
  "Total Case":          { bg: "#0891B2", text: "#FFFFFF", icon: ClipboardList },   // TEAL — total across statuses
};

// Role → ordered KPI list per user's exact spec (Feb 2026)
const KPI_ORDER = {
  admin:   ["Total Leads", "New Case", "Document Pending", "Work in Process", "Sent for Approval", "Approved", "Completed", "Not Approved", "On Hold", "Chalan Pending", "Cancel", "Total Case"],
  process: ["New Case", "Document Pending", "Work in Process", "Sent for Approval", "Approved", "Completed", "Not Approved", "On Hold", "Chalan Pending", "Cancel"],
  connector: ["Total Leads", "New Case", "Document Pending", "Work in Process", "Sent for Approval", "Approved", "Completed", "Not Approved", "On Hold", "Chalan Pending", "Cancel", "Today's Order"],
};

// Map KPI label → status query param (some KPIs are aggregates, not statuses)
const KPI_TO_STATUS = {
  "New Case": "New Case",
  "Document Pending": "Document Pending",
  "Work in Process": "Work in Process",
  "Sent for Approval": "Sent for Approval",
  "Approved": "Approved",
  "Completed": "Completed",
  "Not Approved": "Not Approved",
  "On Hold": "On Hold",
  "Chalan Pending": "Chalan Pending",
  "Cancel": "Cancel",
};

const PIE_COLORS = ["#2563EB", "#F97316", "#EC4899", "#166534", "#22C55E", "#7C4A17", "#EF4444", "#FFF6D1", "#6B7280"];

// Circular KPI card — the ENTIRE card is filled with the KPI color.
function CircularKPI({ label, value, onClick }) {
  const s = KPI_STYLE[label] || {};
  const Icon = s.icon || ClipboardList;
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-2 group focus:outline-none"
      data-testid={`kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
      title={label}
    >
      <div
        className="w-32 h-32 md:w-36 md:h-36 rounded-full flex flex-col items-center justify-center shadow-lg ring-1 ring-black/5 transition-all duration-200 group-hover:scale-105 group-hover:shadow-xl"
        style={{ backgroundColor: s.bg, color: s.text }}
      >
        <Icon size={28} className="opacity-80 mb-1" />
        <div className="font-heading text-3xl md:text-4xl font-extrabold leading-none tabular">{value ?? 0}</div>
      </div>
      <div className="text-[12px] md:text-sm font-bold text-center max-w-[130px] leading-tight">{label}</div>
    </button>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const isEmployee = user?.role === "employee";
  const isConnector = user?.role === "connector";
  const [data, setData] = useState(null);
  const roleKey = isAdmin ? "admin" : isEmployee ? "process" : "connector";

  useEffect(() => {
    (async () => {
      try {
        const endpoint = isAdmin ? "/dashboard/admin" : isEmployee ? "/dashboard/employee" : "/dashboard/connector";
        const r = await api.get(endpoint);
        setData(r.data);
      } catch (e) {
        console.warn("Failed to fetch dashboard", e);
      }
    })();
  }, [isAdmin, isEmployee]);

  if (!data) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const { kpi, recent_leads } = data;

  // Get value for a KPI label from server response
  const kpiValue = (label) => {
    if (label === "Total Leads" || label === "Total Case") return kpi.total_leads ?? 0;
    if (label === "Today's Order") return kpi.today_orders ?? 0;
    return kpi[label] ?? 0;
  };

  // Where does a KPI card navigate?
  const kpiTarget = (label) => {
    if (label === "Total Leads" || label === "Total Case") return "/leads";
    if (label === "Today's Order") return `/leads?today=1`;
    const st = KPI_TO_STATUS[label];
    return st ? `/leads?status=${encodeURIComponent(st)}` : "/leads";
  };

  const kpiList = KPI_ORDER[roleKey] || KPI_ORDER.connector;

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Dashboard</h1>
          <p className="text-sm text-muted-foreground">
            {isAdmin ? "Overview across the organization" :
              isConnector ? "Your orders overview" : "Your assigned cases overview"}
          </p>
        </div>
      </div>

      {/* Circular KPIs — 11 (admin/connector) or 9 (process) fully-filled circles */}
      <div className="flex flex-wrap items-start justify-center gap-x-5 gap-y-6 md:gap-x-7 md:gap-y-8 py-2">
        {kpiList.map((label) => (
          <CircularKPI
            key={label}
            label={label}
            value={kpiValue(label)}
            onClick={() => navigate(kpiTarget(label))}
          />
        ))}
      </div>

      {isAdmin && data.monthly_chart && (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="card p-5 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Orders (Last 6 months)</h3>
              <BarChart3 size={16} className="text-muted-foreground" />
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <BarChart data={data.monthly_chart}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="month" stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <YAxis stroke="hsl(var(--muted-foreground))" fontSize={12} />
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Bar dataKey="orders" fill="#2563EB" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="card p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-bold text-lg">Status Breakdown</h3>
              <PieIcon size={16} className="text-muted-foreground" />
            </div>
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={data.status_chart} dataKey="value" nameKey="name" outerRadius={80} innerRadius={45}>
                    {data.status_chart.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 }} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      )}

      {/* Recent activities */}
      <div className="card overflow-hidden">
        <div className="p-5 border-b border-border flex items-center justify-between">
          <h3 className="font-heading font-bold text-lg">Recent {isAdmin ? "Leads" : "My Orders"}</h3>
          <Link to="/leads" className="text-sm link-blue">View all →</Link>
        </div>
        <div className="overflow-x-auto">
          <table className="table-clean">
            <thead>
              <tr>
                <th>Lead ID</th>
                <th>Client</th>
                <th>Phone</th>
                {isAdmin && <th>Connector</th>}
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody data-testid="recent-leads-table">
              {recent_leads.length === 0 && (
                <tr><td colSpan={isAdmin ? 6 : 5} className="text-center text-muted-foreground py-8">No recent leads</td></tr>
              )}
              {recent_leads.map((l) => (
                <tr key={l.id}>
                  <td><Link to={`/leads/${l.id}`} className="font-mono link-blue">{l.lead_number}</Link></td>
                  <td className="font-semibold">{l.client_name}</td>
                  <td className="tabular">{l.phone}</td>
                  {isAdmin && <td>{l.connector_name}</td>}
                  <td><StatusBadge status={l.status} /></td>
                  <td className="text-muted-foreground text-xs">{new Date(l.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
